import {uploadFileAPI} from "../API/KB/Files";
import {getRandomBetween, RAW_FILES_URL} from "../utils";
import {deepClone, isMobile, isPredominantlyCyrillic, parseJSON, RESOURCE, sleep} from "../App/utils";
import Tooltip from "@mui/material/Tooltip";
import {IconButton, MenuItem, Select, Typography} from "@mui/material";
import {ArrowBackIos, ArrowForwardIos, OpenInNew} from "@mui/icons-material";
import React from "react";
import {getKBImageURL} from "../InputTools/ImageToText";
import {translate} from "../API/OpenAIAPI";
import {MESSAGE} from "../App/constants";
import {PIPE_API_BASE_URL} from "../API/Pipe";
import {VisualModels} from "../constants";
import {signKBTransactionJWT} from "../Crypto/utils";


let blocked = false;
const refreshOnLoad = (images) => {
    if (blocked || images.filter(image => image.loading)?.length >= 15) {
        blocked = true;
        alert("Too many images, browser blocked! Please refresh the page to continue!")
        return true;
    }
}
export const minSeed = -9223372036854775;
export const maxSeed = 18446744073709551;

export const ModelSelect = ({ mode, selectedId, setSelectedId, pipesIds, setSamplingSteps, defaultSamplingSteps, setRotatePipes, setSystemAlert, setTool, setPrompt }) => (
    <Select
        value={selectedId || ''}
        onChange={(event) => {
            if (setRotatePipes) setRotatePipes(false)
            setSelectedId(event.target.value);
            setSamplingSteps(defaultSamplingSteps);
            if (event.target.value === 'stability.sdxlInpaint') {
                setPrompt('Blue eyes')
                setTool('Pencil')
                setPrompt('')
                setSystemAlert({msg: 'Select the object you want to recolor with your mouse and type a new color prompt', type: 'success', duration: 30000, disableClickaway: true})
            } else if (event.target.value === 'runwayml.sdxlInpaint') {
                setTool('Pencil')
                setPrompt('Christmas tree')
                setSystemAlert({msg: 'Select with the mouse the area you want to decorate and type a prompt', type: 'success', duration: 30000, disableClickaway: true})
            } else if (event.target.value === 'diffusers.sdxlInpaint') {
                setTool('Pencil')
                setPrompt('Christmas snow')
                setSystemAlert({msg: 'Select the background with the mouse and type a new background prompt', type: 'success', duration: 30000, disableClickaway: true})
            }

        }}
        style={{marginTop: '10px', width: '100%', height: '30px'}}
    >
        {pipesIds.map((pipeId) => (
            <MenuItem key={pipeId} value={pipeId}>
                <Typography fontSize={12} style={{paddingTop: '3px'}}>

                    {
                        pipeId === 'stability.sdxlInpaint' && 'Recolor Object' ||
                        pipeId === 'runwayml.sdxlInpaint' && 'Add Decoration' ||
                        pipeId === 'diffusers.sdxlInpaint' && 'Change Background' ||
                        VisualModels?.[pipeId]?.name
                    }

                </Typography>
            </MenuItem>
        ))}
    </Select>
);

export const blobUrlToFilename = (blobUrl) => blobUrl.split('/').pop() + '.png';
export const urlToFilename = (url) => url.split('/').pop();

export async function blobUrlToFile(blobUrl) {
    const response = await fetch(blobUrl);
    const blob = await response.blob();

    const filename = blobUrlToFilename(blobUrl);
    return new File([blob], filename, { type: blob.type });
}

export const base64StringToFile = (base64String, filename) => {
    let arr = base64String.split(',');
    let mime = arr[0].match(/:(.*?);/)[1];
    let bstr = atob(arr[1]);
    let n = bstr.length;
    let u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type: mime});
}

export function getRequestId() {
    return getRandomBetween(1, 10**12)
}

const pageRandomSeed = getRandomBetween(1,10**12)

export const createMaskFileName = () => `tmp_file_to_delete_mask_${pageRandomSeed}.png`;

export const handleKeyDown = (event, tool, deleteRef) => {
    if (tool === 'Select' && (event.key === 'Delete' || event.key === 'Backspace')) {
        deleteRef?.current?.click();
    }
}
export const saveBlobImageToS3 = async ({ blobURL, setLoading, kbId}) => {
    setLoading(true);
    const file = await blobUrlToFile(blobURL);
    await uploadFileAPI(file, 'files');
    const publicURL = getKBImageURL(file.name, kbId);
    setLoading(false);
    return publicURL;
}

export function createHandleFileUpload ({setLoading, setImages, setSystemAlert, kbId}) {
    return async (event) => {
        const file = event.target.files[0];
        if (file) {
            setLoading(true);
            try {
                const blobURL = URL.createObjectURL(file);
                const publicURL = await saveBlobImageToS3({ blobURL, setLoading, kbId });
                // Update the images state with the new image URL
                setImages((prevImages) => [
                    ...prevImages.map((img) => ({ ...img, selected: false })),
                    { url: publicURL, selected: true }
                ]);
            } catch (error) {
                setSystemAlert({ message: 'Failed to upload image.', severity: 'error' });
            }
            setLoading(false);
        }
    };
}


export const saveMaskFile = async ({maskName, maskFiles, instanceId}) => {
    if (maskFiles[instanceId]?.type) {
        maskFiles[instanceId] = new File([maskFiles[instanceId]], maskName, {type: maskFiles[instanceId].type});
    }

    await uploadFileAPI(maskFiles[instanceId], 'files');
}

async function calculateSHA256(arrayBuffer) {
    // Use the SubtleCrypto API to calculate the hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);

    // Convert the hash to a hexadecimal string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
}

export const prepareMaskFile = async ({sketch, canvas, instanceId, maskFiles}) => {
    if (!sketch[instanceId]) return;

    return new Promise((resolve, reject) => {
        const maskImage = new Image();

        maskImage.src = sketch[instanceId].toDataURL();

        maskImage.onload = async () => {
            if (!canvas[instanceId]) {
                canvas[instanceId] = document.createElement('canvas');
                canvas[instanceId].width = 1024;
                canvas[instanceId].height = 1024;
            }

            const context = canvas[instanceId].getContext('2d');
            context.fillStyle = 'black';
            context.fillRect(0, 0, canvas[instanceId].width, canvas[instanceId].height);
            context.drawImage(maskImage, 0, 0, canvas[instanceId].width, canvas[instanceId].height);

            let base64NewData = canvas[instanceId].toDataURL();
            maskFiles[instanceId] = base64StringToFile(base64NewData, 'maskFileName');
            resolve(true)
        };
    })
}

export const handleSketchChange = async ({sketch, canUndo, setCanUndo, forceUpdate, setCanRedo, canRedo, canvas, instanceId, maskFiles}) => {
    if (!sketch[instanceId]) return;
    if (sketch[instanceId].canUndo() !== canUndo) {
        setCanUndo(sketch[instanceId].canUndo());
    }
    if (sketch[instanceId].canRedo() !== canRedo) {
        setCanRedo(sketch[instanceId].canRedo());
    }

    await prepareMaskFile({sketch, canvas, instanceId, maskFiles});

    forceUpdate();
}

export function updateHeight({boxRef, divRef, advancedOpen}) {
    if (boxRef.current && divRef.current) {
        let boxHeight = boxRef.current.offsetHeight;
        if (isMobile) {
            boxHeight+= 450;
            if (advancedOpen) boxHeight+= 270
        }

        divRef.current.style.height = `${boxHeight}px`;
    }
}

export function updateInpaintSize({setInpaintSize, imageUrl}) {
    if (!imageUrl) return;
    const img = new Image();

    img.onload = function() {
        // inpaint image size must be divisible by 8
        let width = Math.floor(this.width / 8) * 8;
        let height = Math.floor(this.height / 8) * 8;

        if (width < 1024 && height < 1024) {
            width = 1024;
            height = 1024;
        }

        setInpaintSize({width, height})
    }

    img.src = imageUrl;
}

export function convertToBlobURL(url) {
    if (!url?.startsWith('https')) return url;

    // Define the base part of the public URL that needs to be replaced
    const basePublicURL = 'https://file.openkbs.com/files/';

    // Extract the unique part of the URL (assuming it's the part after '/files/')
    let uniquePart = url.substring(basePublicURL.length);

    // Remove the kbId and the following slash from the unique part
    uniquePart = uniquePart.replace(/^[^/]*\//, '');

    // Remove any file extension from the unique part
    uniquePart = uniquePart.replace(/\.[^/.]+$/, '');

    // Get the current browser location's host and protocol
    const currentLocation = window.location;
    const blobBaseURL = `blob:${currentLocation.protocol}//${currentLocation.host}`;

    // Construct the blob URL
    const blobURL = `${blobBaseURL}/${uniquePart}`;

    return blobURL;
}

export const onImageClick = ({
                                 url,
                                 setImages,
                                 imageUrl,
                                 maskTempURL,
                                 setPrompt,
                                 setSamplingSteps,
                                 setNegativePrompt,
                                 setSelectedInpaintId,
                                 setSelectedGenerationId,
                                 setSeed,
                                 setTool,
                                 setMode,
                                 images
}) => {
    let myUrl = url;

    if (typeof myUrl !== 'string') return;

    // if images are saved by blob url, convert myUrl if not found in images
    if (!images.some(o => o.url === myUrl) && images.some(o => o.url === convertToBlobURL(myUrl))) {
        myUrl = convertToBlobURL(myUrl)
    }

    setImages(prev => prev.map(image => {
        if (image.url === myUrl) {
            return {...image, selected: true}
        } else {
            return {...image, selected: false}
        }
    }))

    if (myUrl !== imageUrl && myUrl !== maskTempURL) {
        const imageFound = images.find(image => image.url === myUrl);

        if (imageFound) {
            if (imageFound.tool !== undefined) setTool(imageFound.tool)
            if (imageFound.mode !== undefined) setMode(imageFound.mode)
            if (imageFound.prompt !== undefined) setPrompt(imageFound.prompt)
            if (imageFound.negativePrompt !== undefined) setNegativePrompt(imageFound.negativePrompt)
            if (imageFound.samplingSteps !== undefined) setSamplingSteps(imageFound.samplingSteps)
            if (imageFound.seed !== undefined) setSeed(imageFound.seed)
            if (imageFound.selectedInpaintId !== undefined) setSelectedInpaintId(imageFound.selectedInpaintId)
            if (imageFound.selectedGenerationId !== undefined) setSelectedGenerationId(imageFound.selectedGenerationId)
        }
    }
}

let realtimeTimeout;
let realtimeInProgress = false;
export const handleSubmitRealtimeGeneration = async ({prompt, realtimeGenerationPipe, KB, setRealtimeImage}) => {
    return null; // Disable realtime generation
    if (realtimeInProgress) return;
    realtimeInProgress = true;
    const requestId = getRequestId();
    const selectedGeneration = Object.keys(VisualModels).map(key => ({
        pipeId: key,
        ...VisualModels[key]
    })).find(o => o.modelType === 'generation' && o.pipeId === realtimeGenerationPipe);

    let {pipeId, url} = selectedGeneration && selectedGeneration || {};

    let promptTranslated = await translateIfNeeded(prompt, KB);

    let pipeURL = `${url}?prompt=${encodeURIComponent(promptTranslated)}` +
        `&s=4` +
        '&requestId=' + requestId;

    const transactionJWT = await signKBTransactionJWT({modelId: pipeId, ...KB, message: 'image generation'})
    pipeURL += `&transactionJWT=${encodeURIComponent(transactionJWT)}`;

    let response = await fetch(pipeURL);

    realtimeInProgress = false;
    const blob = await response.blob();
    const urlBlob = URL.createObjectURL(blob);

    setRealtimeImage(urlBlob);
    if (realtimeTimeout) clearTimeout(realtimeTimeout)

    realtimeTimeout = setTimeout(() => setRealtimeImage(null), 5000)
}

const translateIfNeeded = async (prompt, KB) => {
    let promptTranslated = prompt;

    if (isPredominantlyCyrillic(promptTranslated)) {
        const res = await translate(prompt, KB);
        if (res.translation) promptTranslated = res.translation;
    }

    return promptTranslated
}


export const handleSubmitUpscale = async ({ prompt, negativePrompt, images, samplingSteps, seed, selectedUpscaleId,
    setImages, KB, setSystemAlert, mode, tool, imageUrl, imageSize, imageClick, submitUpscaleRef }) => {
    if (refreshOnLoad(images)) return;
    return new Promise(async (resolve) => {
        const requestId = getRequestId();
        // Add a loading image immediately after the request is sent
        setImages((prev) => [...prev, {
            url: 'loading_image_' + Math.random(),
            selected: false,
            loading: true,
            requestId,
            prompt,
            negativePrompt,
            samplingSteps,
            seed,
            selectedUpscaleId,
            mode,
            tool
        }]);

        let promptTranslated = await translateIfNeeded(prompt, KB);
        let negativePromptTranslated = await translateIfNeeded(negativePrompt, KB);

        const selectedUpscale = Object.keys(VisualModels).map(key => ({
            pipeId: key,
            ...VisualModels[key]
        })).find(o => o.modelType === 'upscale' && o.pipeId === selectedUpscaleId);

        let {pipeId, url} = selectedUpscale  || {};

        const publicImageURL = getPublicImageURL(imageUrl, KB.kbId);

        let pipeURL = `${url}?prompt=${encodeURIComponent(promptTranslated)}` +
            `&s=${encodeURIComponent(samplingSteps)}` +
            `&np=${encodeURIComponent(negativePromptTranslated)}` +
            `&image=${publicImageURL}` +
            '&requestId=' + requestId;

        if (seed === parseInt(seed)) pipeURL += `&seed=${encodeURIComponent(seed)}`

        const transactionJWT = await signKBTransactionJWT({modelId: pipeId, ...KB, message: 'image generation'})
        pipeURL += `&transactionJWT=${encodeURIComponent(transactionJWT)}`;

        let response;
        try {
            response = await fetch(pipeURL);
        } catch (e) {
            console.log('Generation failed', e.message)
            return;
        }


        if (!response?.ok) {
            const {error} = await response.json();
            setSystemAlert({msg: error, type: 'error', duration: 10000});
        }


        if (response?.status === 499) {
            // handle payment form
        }

        if (!response || response?.status >= 400) {
            setImages((prevImages) => prevImages.filter(image => image.requestId !== requestId) );
            return;
        }

        const blob = await response.blob();
        const urlBlob = URL.createObjectURL(blob);

        saveBlobImageToS3({ blobURL: urlBlob, setLoading: () => {}, kbId: KB.kbId });

        // onImageClick(url)

        const img = new Image();
        img.onload = function() {
            setImages((prev) => prev.map(image => {
                if (image.requestId === requestId) {
                    return {...image, url: urlBlob, selected: image.selected, loading: false}
                } else {
                    return {...image, selected: image.selected};
                }
            }));
            imageClick(urlBlob)
            resolve(urlBlob)
            if (submitUpscaleRef.current) {
                submitUpscaleRef.current.lastResponse = {url: urlBlob};
                submitUpscaleRef.current.started = false;
            }
        };
        img.src = urlBlob;
    })
}

let lastRequestId = null;
let aggregatedContent = '';
export const createNewTokensHandler = ({kbId, RequestChatAPI, setSystemAlert,
    setPrompt, submitGenerationRef, setMode, setNegativePrompt, submitInpaintRef, submitUpscaleRef,
    setSamplingSteps, setSeed, setSelectedInpaintId, setRotatePipes, advancedMenuRef, setAdvancedOpen, advancedOpen,
    mode, setBlinkAnimationImportImage, importImageRef, setTool, openImageRef, setMessages, setRotateSeed,
    setSelectedGenerationId, messages, responseCommandRef, imageClick, imageDelete, imageVisible
}) => {
    return async (e) => {
        const tokens = e?.detail?.tokens.filter(item => item !== '').map(msg => parseJSON(msg))
        const requestId = e?.detail?.requestId

        // Reset aggregatedContent on new request
        if (requestId !== lastRequestId) {
            aggregatedContent = '';
            lastRequestId = requestId;
        }

        for (let token of tokens) {
            const {content, done} = token;

            if (content) {
                aggregatedContent += content;
            }

            if (done) {
                // aggregatedContent = ''
            }

            const openAdvanced = async () => {
                if (!advancedOpen) {
                    setAdvancedOpen(true)
                }
            }

            const appendMessage = (msg) => {
                setTimeout(() => {
                    setSystemAlert({msg, type: 'success', duration: 30000, disableClickaway: true})
                    setMessages(prev => {
                        let newMessages = deepClone(prev)
                        newMessages[newMessages.length - 1].content = newMessages[newMessages.length - 1]?.content + '\n' + msg
                        return newMessages;
                    })
                }, 1000)
            }

            const actions = [
                [/\$?setPrompt\("(.*)"?\)?/, (match) => {
                    const txt = match[1];
                    if (txt && !txt.endsWith('"') && !txt.endsWith('")')) {
                        setPrompt(txt);
                    } else if (txt && txt.endsWith('")')) {
                        return true;
                    }
                }],
                [/\$?setNegativePrompt\("(.*)"?\)?/, async (match) => {
                    await openAdvanced();
                    const txt = match[1];
                    if (txt && !txt.endsWith('"') && !txt.endsWith('")')) {
                        setNegativePrompt(txt);
                    } else if (txt && txt.endsWith('")')) {
                        return true;
                    }
                }],
                [/\$?setSamplingSteps\((.*)\)/, async (match) => {
                    await openAdvanced();
                    setSamplingSteps(match[1])
                    return true;
                }],

                [/\$?clickEditTab\(?\)?/, () => {
                    setMode('Edit')
                    return true;
                }],
                [/\$?setRotatePipes\((.*)\)/, async (match) => {
                    await openAdvanced();
                    setRotatePipes(!!match[1])
                    return true;
                }],
                [/\$?setSelectedInpaintId\("(.*)"\)/, async (match) => {
                    await openAdvanced();
                    setRotatePipes(false);
                    setSelectedInpaintId(match[1])
                    return true;
                }],
                [/\$?setSelectedGenerationId\("(.*)"\)/, async (match) => {
                    await openAdvanced();
                    setSelectedGenerationId(match[1])
                    return true;
                }],
                [/\$?generate\.response\((.*)\)/, async (match) => {
                    setMode('Generate');
                    await sleep(200)
                    await clickButtonAndWait(submitGenerationRef, 30000, (match[1] ? parseInt(match[1]) : 1))
                    responseCommandRef.current = null;
                    return

                }],
                [/\$?inpaint\.response\(?\)?/, async () => {
                    setMode('Edit');
                    await sleep(200)
                    await clickButtonAndWait(submitInpaintRef, 30000)
                    responseCommandRef.current = null;
                    return
                }],
                [/\$?upscale\.response\(?\)?/, async () => {
                    setMode('Upscale');
                    await sleep(200)
                    await clickButtonAndWait(submitUpscaleRef, 30000)
                    responseCommandRef.current = null;
                    return
                }],
                [/\$?clickAdvancedButton\(?\)?/, () => {
                    advancedMenuRef?.current?.focus()
                    advancedMenuRef?.current?.click()
                    return true;
                }],

                [/\$?clickEditTab\(?\)?/, () => {
                    setMode('Edit')
                    return true;
                }],
                [/\$?clickGenerateTab\(?\)?/, () => {
                    setMode('Generate')
                    return true;
                }],
                [/\$?clickUpscaleTab\(?\)?/, () => {
                    setMode('Upscale')
                    return true;
                }],
                [/\$?setSelectedInpaintId\("(.*)"\)/, async (match) => {
                    await openAdvanced();
                    setRotatePipes(false);
                    setSelectedInpaintId(match[1])
                    return true;
                }],

                [/\$?imageClick\("(.*)"\)/, async (match) => {
                    imageClick(match[1])
                    return true;
                }],

                [/\$?imageDelete\("(.*)"\)/, async (match) => {
                    imageDelete(match[1])
                    return true;
                }],

                [/\$?imageVisible\("(.*)"\)/, async (match) => {
                    imageVisible(match[1])
                    return true;
                }],

                [/\$?openImageImport\(?\)?/, () => {
                    setBlinkAnimationImportImage(Math.random())
                    importImageRef.current.focus()
                    return true;
                }],
                [/\$?openCropTool\(?\)?/, () => {
                    setMode('Edit')
                    setTool('Crop')
                    return true;
                }],
                [/\$?openPencilTool\(?\)?/, () => {
                    setMode('Edit')
                    setTool('Pencil')
                    appendMessage('Use the mouse to select the inpaint area of the image. When selecting an object, ensure to include a margin around it for complete coverage.')
                    return true;
                }],
                [/\$?openSelectedImageInNewTab\(?\)?/, () => {
                    setMode('Edit')
                    openImageRef?.current?.click();
                    return true;
                }],
                [/\$?rotateSeed\((.*)\)/, async (match) => {
                    await openAdvanced();
                    setRotateSeed(match[1] === 'true')
                    return true;
                }],


            ];

            let commandFound = false;
            for (let [regex, action] of actions) {
                const match = aggregatedContent?.match(regex);
                if (match) {
                    commandFound = true;

                    if (match?.[0]?.includes('.response(')) {
                        if (responseCommandRef.current) {
                            console.log('Response commands blocked!')
                            break;
                        }
                        responseCommandRef.current = true
                    }

                    const data = await action(match);

                    if (data) {
                        aggregatedContent = '';
                    }

                    if (typeof data === 'object') {
                        const savedMessages = parseJSON(localStorage.getItem(`kbChat_${kbId}`));
                        RequestChatAPI([...savedMessages, { role: 'system', content: JSON.stringify({data, type: MESSAGE.API_RESPONSE})}]);
                    }

                    break;
                }
            }

            if (!commandFound && aggregatedContent?.length && /\S/.test(aggregatedContent) && !aggregatedContent.trimStart().startsWith('$')) {
                setSystemAlert({msg: extractText(aggregatedContent), type: 'success', duration: 30000, disableClickaway: true, multiline: true})
            }


        }
    };
}

function extractText(input) {
    const trimmedInput = input.trimStart();
    const lines = trimmedInput.split('\n');
    const filteredLines = lines.filter(line => !line.trim().startsWith('$'));
    return filteredLines.join('\n');
}

export const clickButtonAndWait = (ref, timeout = 30000, n = 1) => {
    return new Promise((resolve) => {
        ref?.current?.focus()
        delete ref?.current?.lastResponse;
        ref.current.started = +new Date();

        for (let i = 1; i <= n; i++) {
            ref.current.seed = i > 1 ? getRandomBetween(minSeed, maxSeed) : undefined;
            ref?.current?.click()
        }

        let interval = setInterval(() => {
            if (!ref?.current?.started || ((ref?.current?.started + timeout) < +new Date())) {
                if (ref?.current?.lastResponse) {
                    resolve(deepClone(ref?.current?.lastResponse));
                } else {
                    resolve({timeout: true});
                }

                clearInterval(interval);
            }
        }, 100)
    })
}

export const handleSubmitGeneration = ({
    injectedSeed,
    prompt, negativePrompt, images, samplingSteps, seed, setSeed, rotateSeed, selectedGenerationId,
    setImages, KB, setSystemAlert, mode, tool, imageClick, imageDelete, submitGenerationRef
    }) => {
    if (refreshOnLoad(images)) return;

    const mySeed = injectedSeed ? injectedSeed : seed;

    return new Promise(async (resolve) => {
        const requestId = getRequestId();
        // Add a loading image immediately after the request is sent
        if (rotateSeed) setSeed(getRandomBetween(minSeed, maxSeed));

        setImages((prev) => [...prev, {
            url: 'loading_image_' + Math.random(),
            selected: false,
            loading: true,
            requestId,
            prompt,
            negativePrompt,
            samplingSteps,
            seed: mySeed,
            selectedGenerationId,
            mode,
            tool
        }]);

        let promptTranslated = await translateIfNeeded(prompt, KB);
        let negativePromptTranslated = await translateIfNeeded(negativePrompt, KB);

        const selectedGeneration = Object.keys(VisualModels).map(key => ({
            pipeId: key,
            ...VisualModels[key]
        })).find(o => o.modelType === 'generation' && o.pipeId === selectedGenerationId);

        let {pipeId, url} = selectedGeneration || {};

        let pipeURL = `${url}?prompt=${encodeURIComponent(promptTranslated)}` +
            `&s=${encodeURIComponent(samplingSteps)}` +
            `&np=${encodeURIComponent(negativePromptTranslated)}` +
            '&requestId=' + requestId;

        if (mySeed === parseInt(mySeed)) pipeURL += `&seed=${encodeURIComponent(mySeed)}`

        // Prepare Transaction
        const transactionJWT = await signKBTransactionJWT({modelId: pipeId, ...KB, message: 'image generation'})
        pipeURL += `&transactionJWT=${encodeURIComponent(transactionJWT)}`;

        let response;
        try {
            response = await fetch(pipeURL);
        } catch (e) {
            console.log('Generation failed', e.message)
        }

        if (!response.ok) {
            const { error } = await response.json();
            if (error?.startsWith?.('job failed')) {
                setSystemAlert({ msg: 'Job failed, retrying ...', type: 'error', duration: 3000 });
                await sleep(500)
                try {
                    response = await fetch(pipeURL); // Attempt to fetch again
                    if (!response.ok) {
                        // If still failing after retry, handle error again
                        const { error } = await response.json();
                        setSystemAlert({ msg: error, type: 'error', duration: 10000 });
                        return;
                    }
                } catch (e) {
                    console.log('Retry failed', e.message);
                    return;
                }
            } else {
                // Handle other errors
                setSystemAlert({ msg: error, type: 'error', duration: 10000 });
                if (response?.status === 499) {
                   // to handle here payment form
                }
                setImages((prevImages) => prevImages.filter(image => image.requestId !== requestId) );
                return;
            }
        }


        if (!response || response?.status === 500) {
            setImages((prevImages) => prevImages.filter(image => image.requestId !== requestId) );
            return;
        }

        const blob = await response.blob();
        const urlBlob = URL.createObjectURL(blob);

        saveBlobImageToS3({ blobURL: urlBlob, setLoading: () => {}, kbId: KB.kbId });

        const img = new Image();
        img.onload = function() {
            setImages((prev) => prev.map(image => {
                if (image.requestId === requestId) {
                    return {...image, url: urlBlob, selected: image.selected, loading: false}
                } else {
                    return {...image, selected: image.selected};
                }
            }));
            imageClick(urlBlob)
            resolve(urlBlob)
            if(submitGenerationRef.current) {
                submitGenerationRef.current.lastResponse = {url: urlBlob};
                submitGenerationRef.current.started = false;
            }


        };
        img.src = urlBlob;
    });
}

export const getPublicImageURL = (imageUrl, kbId) => {
    if (imageUrl?.startsWith?.('https')) {
        return imageUrl
    }

    if (imageUrl?.startsWith?.('blob')) {
        return getKBImageURL(blobUrlToFilename(imageUrl), kbId);
    }
}

export const handleSubmitInpaint = async ({
            prompt, negativePrompt, images, samplingSteps, seed, selectedInpaintId, setImages, KB, kbId, sketch, canvas,
            instanceId, maskFiles, inpaintSize, imageUrl, setSystemAlert, mode, setTool,
            rotatePipes, setSelectedInpaintId, inpaintPipes, submitInpaintRef, imageClick, setSeed, rotateSeed
}) => {
    if (refreshOnLoad(images)) return;
    return new Promise(async (resolve) => {
        const requestId = getRequestId();
        // Add a loading image immediately after the request is sent
        if (rotateSeed) setSeed(getRandomBetween(minSeed, maxSeed));
        setImages((prev) => [...prev, {
            url: 'loading_image_' + Math.random(),
            selected: false,
            loading: true,
            requestId,
            prompt,
            negativePrompt,
            samplingSteps,
            seed,
            selectedInpaintId,
            mode,
            tool: null
        }]);

        if (rotatePipes) {
            const idx = inpaintPipes.indexOf(selectedInpaintId);
            setSelectedInpaintId(inpaintPipes[(idx+1)%inpaintPipes.length]);
        }

        let promptTranslated = await translateIfNeeded(prompt, KB);
        let negativePromptTranslated = await translateIfNeeded(negativePrompt, KB);

        const maskName = createMaskFileName();

        await prepareMaskFile({sketch, canvas, instanceId, maskFiles});

        await saveMaskFile({maskName, maskFiles, instanceId});

        const maskUniqueURL = `${RAW_FILES_URL}/files/${kbId}/${maskName}`;

        const selectedInpaint = Object.keys(VisualModels).map(key => ({
            pipeId: key,
            ...VisualModels[key]
        })).find(o => o.modelType === 'inpaint' && o.pipeId === selectedInpaintId);

        let {pipeId, url} = selectedInpaint || {};


        const publicImageURL = getPublicImageURL(imageUrl, kbId);

        let pipeURL = `${url}?prompt=${encodeURIComponent(promptTranslated)}` +
            `&w=${encodeURIComponent(inpaintSize.width)}` +
            `&h=${encodeURIComponent(inpaintSize.height)}` +
            `&s=${encodeURIComponent(samplingSteps)}` +
            `&np=${encodeURIComponent(negativePromptTranslated)}` +
            '&requestId=' + requestId +
            `&mask_image=${(maskUniqueURL)}` +
            `&image=${publicImageURL}`;

        if (seed === parseInt(seed)) {
            pipeURL += `&seed=${seed}`
        }

        const transactionJWT = await signKBTransactionJWT({modelId: pipeId, ...KB, message: 'image generation'})
        pipeURL += `&transactionJWT=${encodeURIComponent(transactionJWT)}`;

        let response;
        try {
            response = await fetch(pipeURL);
        } catch (e) {
            console.log('Generation failed', e.message)
        }

        if (!response?.ok) {
            const {error} = await response.json();
            setSystemAlert({msg: error, type: 'error', duration: 10000});
        }

        if (response?.status === 499) {
            // handle payment form
        }

        // cleanup the mask file
        // await deleteRawKBFile(maskName, 'files')
        delete maskFiles[instanceId];

        if (!response || response?.status >= 400) {
            setImages((prevImages) => prevImages.filter(image => image.requestId !== requestId) );
            return;
        }

        const blob = await response.blob();
        const urlBlob = URL.createObjectURL(blob);

        saveBlobImageToS3({ blobURL: urlBlob, setLoading: () => {}, kbId: KB.kbId });

        setTool(null)

        const img = new Image();
        img.onload = function() {
            setImages((prev) => prev.map(image => {
                if (image.requestId === requestId) {
                    return {...image, url: urlBlob, selected: image.selected, loading: false}
                } else {
                    return {...image, selected: image.selected};
                }
            }));
            imageClick(urlBlob)
            resolve(urlBlob)
            if (submitInpaintRef.current) {
                submitInpaintRef.current.lastResponse = {url: urlBlob};
                submitInpaintRef.current.started = false;
            }
        };
        img.src = urlBlob;
    });
}

export const handleSubmitCrop = async ({
    setLoading, cropParams, sketch, canvas, instanceId, maskFiles, imageUrl, kbId, setTool,
    setImages, mode, setSystemAlert, imageClick, KB
}) => {
    setLoading(true);

    const requestId = getRequestId();

    // Add a loading image immediately after the request is sent
    setImages((prev) => [...prev, {
        url: 'loading_image_' + Math.random(),
        selected: false,
        loading: true,
        requestId,
        mode
    }]);

    await prepareMaskFile({sketch, canvas, instanceId, maskFiles});

    const { x, y, width, height} = cropParams;

    const publicImageURL = getPublicImageURL(imageUrl, kbId);
    const pipeId = 'crop';
    let cropURL = `${PIPE_API_BASE_URL}/${pipeId}` +
        `?image=${encodeURIComponent(publicImageURL)}` +
        `&x=${encodeURIComponent(x)}` +
        `&y=${encodeURIComponent(y)}` +
        `&width=${encodeURIComponent(width)}` +
        `&height=${encodeURIComponent(height)}`;

    const transactionJWT = await signKBTransactionJWT({modelId: pipeId, ...KB, message: 'image generation'})
    cropURL += `&transactionJWT=${encodeURIComponent(transactionJWT)}`;

    const response = await fetch(cropURL);

    if (!response?.ok) {
        const {error} = await response.json();
        setSystemAlert({msg: error, type: 'error', duration: 10000});
    }

    if (response?.status === 499) {
        // handle payment form
    }

    if (!response || response?.status >= 400) {
        setImages((prevImages) => prevImages.filter(image => image.requestId !== requestId) );
        return;
    }


    const blob = await response.blob();
    const urlBlob = URL.createObjectURL(blob);

    saveBlobImageToS3({ blobURL: urlBlob, setLoading: () => {}, kbId });

    const img = new Image();

    img.onload = function() {
        setImages((prev) => prev.map(image => {
            if (image.requestId === requestId) {
                return {...image, url: urlBlob, selected: image.selected, loading: false}
            } else {
                return {...image, selected: image.selected};
            }
        }));
        imageClick(urlBlob)
    };
    img.src = urlBlob;

    setTool(null)
    setLoading(false);
}

export const DownloadButton = ({imageUrl, kbId, openImageRef, style={}}) => {
    if (!imageUrl) return null;
    const handleDownload = async () => {
        let url = imageUrl?.startsWith?.('blob') ? getKBImageURL(blobUrlToFilename(imageUrl), kbId) : imageUrl;

        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        // link.setAttribute('download', filename); // or any other extension
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    return (
        <Tooltip title="Open in new window">
            <IconButton ref={openImageRef} onClick={handleDownload}>
                <OpenInNew style={style} />
            </IconButton>
        </Tooltip>
    );
};

export const createHandleImageImport = ({sketch, instanceId, setImportedImage, setTool}) => (event) => {
    const file = event.target.files[0];
    event.target.value = null;
    setTool('Select')
    if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => { // Make the function async
            const img = new Image();
            img.onload = async () => { // Make the onload handler async
                // Dynamically import the fabric library
                const { fabric } = await import("fabric");
                const fabricImage = new fabric.Image(img);
                fabricImage.set({
                    left: 50,
                    top: 50,
                    scaleX: 0.25,
                    scaleY: 0.25,
                    selectable: true,
                });
                sketch[instanceId]?._fc.add(fabricImage);
                setImportedImage(fabricImage);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }
};

export const createHandleImportImageSave = ({sketch, instanceId, imageClick, imageSize, imageUrl, importedImage, setImportedImage, setPrepareImport, setTool, setImages, setLoading, kbId, scale = 1, setBlockingLoading}) => async () => {
    const baseImageLoaded = new Promise((resolve, reject) => {
        setBlockingLoading(true)
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = async () => {
            sketch[instanceId]._fc.setWidth(imageSize.width);
            sketch[instanceId]._fc.setHeight(imageSize.height);

            const { fabric } = await import("fabric");
            const fabricImage = new fabric.Image(img);

            sketch[instanceId]._fc.getObjects().forEach(function(object) {
                object.set({
                    scaleX: object.scaleX * scale,
                    scaleY: object.scaleY * scale,
                    left: object.left * scale,
                    top: object.top * scale
                });
            });

            fabricImage.set({selectable: false});
            sketch[instanceId]._fc.setBackgroundImage(fabricImage, sketch[instanceId]._fc.renderAll.bind(sketch[instanceId]._fc));
            resolve();
        };
        img.onerror = (e) => {
            if (imageUrl.startsWith?.('blob')) {
                const originalUrl = getKBImageURL(blobUrlToFilename(imageUrl), kbId);
                img.src = originalUrl;
            }
            reject(new Error('Failed to load image '));
        };
        img.src = imageUrl;

        return img;
    });

    try {
        await baseImageLoaded;
        sketch[instanceId]._fc.renderAll();

        const dataURL = sketch[instanceId]._fc.toDataURL({
            format: 'png',
            quality: 1.0,
        });

        const blob = await (await fetch(dataURL)).blob();
        const blobURL = URL.createObjectURL(blob);

        const publicURL = await saveBlobImageToS3({ blobURL, setLoading, kbId });

        if (importedImage) {
            setImportedImage(null);
            setPrepareImport(null)
            sketch[instanceId]?._fc.clear();
            setTool(null)
        }

        setImages((prevImages) => [
            ...prevImages,
            { url: publicURL, selected: false }
        ]);

        imageClick(publicURL)
        setBlockingLoading(false)
    } catch (err) {
        console.error("Error generating image:", err);
    }
};

export const renderPrevArrow = (showArrows, goToPreviousImage, selectedImageIndex, images) => !!images?.length && showArrows && (
        <IconButton
            style={{
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 999999,
            }}
            onClick={goToPreviousImage}
            disabled={selectedImageIndex === 0}
        >
            <ArrowBackIos style={{color: selectedImageIndex === 0 ? 'grey' : 'inherit'}}/>
        </IconButton>
    )


export const renderNextArrow = (showArrows, goToNextImage, selectedImageIndex, images) => !!images?.length && showArrows && (
        <IconButton
            style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 999999,
            }}
            onClick={goToNextImage}
            disabled={selectedImageIndex === images.length - 1}
        >
            <ArrowForwardIos style={{color: (selectedImageIndex === images.length - 1) ? 'grey' : 'inherit'}} />
        </IconButton>
    )